import { type SubmitEvent, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { formatDistanceToNow } from 'date-fns';
import { AlertCircle, Archive, MoreHorizontal, Plus, Trash2, Wand2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import {
  type CreatePlanRequest,
  type ListPlansFilters,
  type NetworkPlan,
  type PlanState,
  useArchivePlan,
  useCreatePlan,
  useDeletePlan,
  useNetworkPlans,
  useInstantiateTemplate,
} from '@/hooks/useNetworkPlans';
import { preferredContext, useAvailableContextsInOrg } from '@/hooks/useContexts';
import { useActiveContext } from '@/hooks/useActiveContext';
import ConfirmationModal from '@/components/ConfirmationModal';
import { PageContainer } from '@/components/ui/page-container';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  ResponsiveTable,
  type ResponsiveTableColumn,
} from '@/components/ui/responsive-table';
import { AppSheet } from '@/components/ui/app-sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

const STATE_STYLE: Record<PlanState, string> = {
  draft: 'bg-muted text-muted-foreground',
  locked: 'bg-sev-3/20 text-foreground',
  executing: 'bg-brand-soft text-brand',
  completed: 'bg-sev-4/15 text-sev-4',
  failed: 'bg-sev-1/15 text-sev-1',
  cancelled: 'bg-sev-3/30 text-foreground',
};

const STATE_LABEL: Record<PlanState, string> = {
  draft: 'Draft',
  locked: 'Locked',
  executing: 'Executing',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

export default function NetworkPlans() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const organizationId = user?.organizationId;

  const [filters, setFilters] = useState<ListPlansFilters>({});
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const [formContextId, setFormContextId] = useState('');
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formIsTemplate, setFormIsTemplate] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [planToDelete, setPlanToDelete] = useState<NetworkPlan | null>(null);

  const { data: plans, isLoading, isError, error } = useNetworkPlans(organizationId, filters);
  // One organization's contexts: a plan is created against this organization,
  // so another's context could only be rejected on submit.
  const { data: contexts } = useAvailableContextsInOrg(organizationId);
  const { activeContextId } = useActiveContext();

  // Start on the context the user is already working in, leaving a
  // deliberate choice alone once made.
  // Set during render (guarded, so it settles in one pass) rather than in an
  // effect, which would paint the empty field first.
  const preferred = preferredContext(contexts, activeContextId);
  if (!formContextId && preferred) setFormContextId(preferred.id);

  const createMutation = useCreatePlan();
  const deleteMutation = useDeletePlan();
  const archiveMutation = useArchivePlan();
  const instantiateMutation = useInstantiateTemplate();

  const handleInstantiate = (plan: NetworkPlan) => {
    instantiateMutation.mutate(
      { templateId: plan.id, data: { createChange: true } },
      {
        onSuccess: (res) => {
          if (res.cmId) {
            void navigate(`/change-management/${res.cmId}`);
          } else {
            void navigate(`/network-plans/${res.planId}`);
          }
        },
      },
    );
  };

  const handleCreate = async (e?: SubmitEvent) => {
    e?.preventDefault();
    setSubmitError(null);
    if (!formContextId) {
      setSubmitError('Context is required');
      return;
    }
    if (!formTitle.trim()) {
      setSubmitError('Title is required');
      return;
    }
    const next: CreatePlanRequest = {
      contextId: formContextId,
      title: formTitle,
      description: formDescription || undefined,
      isTemplate: formIsTemplate || undefined,
    };
    try {
      await createMutation.mutateAsync(next);
      setFormContextId('');
      setFormTitle('');
      setFormDescription('');
      setFormIsTemplate(false);
      setIsCreateOpen(false);
    } catch {
      setSubmitError('Failed to create plan.');
    }
  };

  const confirmDelete = async () => {
    if (!planToDelete) return;
    try {
      await deleteMutation.mutateAsync(planToDelete.id);
    } catch {
      // ignore — typical optimistic flow
    } finally {
      setPlanToDelete(null);
    }
  };

  const columns = useMemo<readonly ResponsiveTableColumn<NetworkPlan>[]>(
    () => [
      {
        key: 'title',
        label: 'Title',
        hideMobileLabel: true,
        render: (plan) => (
          <div className="min-w-0">
            <span className="flex items-center gap-2">
              <Link
                to={`/network-plans/${plan.id}`}
                className="font-medium hover:underline truncate block"
                onClick={(e) => { e.stopPropagation(); }}
              >
                {plan.title}
              </Link>
              {plan.isTemplate ? <Badge variant="secondary">Template</Badge> : null}
            </span>
            {plan.description ? (
              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                {plan.description}
              </p>
            ) : null}
          </div>
        ),
      },
      {
        key: 'state',
        label: 'State',
        render: (plan) => (
          <Badge className={cn('border-0 font-normal', STATE_STYLE[plan.state])}>
            {STATE_LABEL[plan.state]}
          </Badge>
        ),
      },
      {
        key: 'cm',
        label: 'Change',
        hideAt: 'mobile',
        render: (plan) =>
          plan.cmId ? (
            <Link
              to={`/change-management/${plan.cmId}`}
              className="hover:underline truncate inline-block max-w-[16rem]"
              onClick={(e) => { e.stopPropagation(); }}
            >
              {plan.cmTitle}
            </Link>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        key: 'steps',
        label: 'Steps',
        render: (plan) => (
          <span className="font-mono text-xs">
            {String(plan.completedStepCount)}/{String(plan.stepCount)}
          </span>
        ),
      },
      {
        key: 'created',
        label: 'Created',
        hideAt: 'mobile',
        render: (plan) => (
          <span className="text-xs text-muted-foreground">
            {formatDistanceToNow(new Date(plan.createdAt), { addSuffix: true })}
          </span>
        ),
      },
    ],
    [],
  );

  if (isError) {
    return (
      <PageContainer>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Error loading plans: {error.message}</AlertDescription>
        </Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Network plans"
        subtitle="Plans are normally built inside a change. This lists them directly."
        primary={
          <Button onClick={() => {
            setFormContextId('');
            setFormTitle('');
            setFormDescription('');
            setSubmitError(null);
            setIsCreateOpen(true);
          }}>
            <Plus className="w-4 h-4" />
            <span className="hidden md:inline">Create plan</span>
            <span className="md:hidden">New</span>
          </Button>
        }
      />

      {/* Filters */}
      <div className="bg-card border border-border rounded-lg p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="plan-state-filter" className="text-xs">State</Label>
            <Select
              value={filters.state ?? 'all'}
              onValueChange={(v) => {
                setFilters({ ...filters, state: v === 'all' ? undefined : v });
              }}
            >
              <SelectTrigger id="plan-state-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All states</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="locked">Locked</SelectItem>
                <SelectItem value="executing">Executing</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4 mt-4 pt-3 border-t border-border-subtle">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox
              checked={filters.includeArchived ?? false}
              onCheckedChange={(v) => {
                setFilters({ ...filters, includeArchived: v === true ? true : undefined });
              }}
            />
            <span>Include archived</span>
          </label>
        </div>
      </div>

      <ResponsiveTable<NetworkPlan>
        columns={columns}
        rows={plans ?? []}
        rowKey={(r) => r.id}
        isLoading={isLoading}
        onRowClick={(r) => { void navigate(`/network-plans/${r.id}`); }}
        rowActions={(plan) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => { e.stopPropagation(); }}
                aria-label={`Actions for ${plan.title}`}
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => { e.stopPropagation(); }}>
              {plan.isTemplate ? (
                <DropdownMenuItem
                  onClick={() => { handleInstantiate(plan); }}
                  className="gap-2"
                  disabled={instantiateMutation.isPending}
                >
                  <Wand2 className="w-4 h-4" />
                  Use template
                </DropdownMenuItem>
              ) : null}
              {plan.state === 'draft' ? (
                <DropdownMenuItem
                  onClick={() => { setPlanToDelete(plan); }}
                  className="gap-2 text-destructive focus:text-destructive"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </DropdownMenuItem>
              ) : null}
              {(plan.state === 'completed' ||
                plan.state === 'cancelled' ||
                plan.state === 'failed') &&
              !plan.archivedAt ? (
                <DropdownMenuItem
                  onClick={() => { archiveMutation.mutate(plan.id); }}
                  className="gap-2"
                >
                  <Archive className="w-4 h-4" />
                  Archive
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        empty={
          <EmptyState
            variant="no-data"
            title="No plans yet"
            description="Create a plan to stage a multi-step network change."
            action={
              <Button onClick={() => { setIsCreateOpen(true); }}>
                <Plus className="w-4 h-4" />
                Create plan
              </Button>
            }
          />
        }
      />

      {/* Create modal */}
      <AppSheet open={isCreateOpen} onOpenChange={setIsCreateOpen} title="Create plan">
        <AppSheet.Body>
          <form
            id="plan-create-form"
            onSubmit={(e) => { void handleCreate(e); }}
            className="space-y-4"
          >
            {submitError ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            ) : null}

            <div className="space-y-1.5">
              <Label htmlFor="plan-context">
                Context <span className="text-destructive">*</span>
              </Label>
              <Select value={formContextId} onValueChange={setFormContextId}>
                <SelectTrigger id="plan-context">
                  <SelectValue placeholder="Select a context" />
                </SelectTrigger>
                <SelectContent>
                  {contexts?.map((ctx) => (
                    <SelectItem key={ctx.id} value={ctx.id}>
                      {ctx.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="plan-title">
                Title <span className="text-destructive">*</span>
              </Label>
              <Input
                id="plan-title"
                value={formTitle}
                onChange={(e) => { setFormTitle(e.target.value); }}
                placeholder="e.g., VLAN 100 deployment — Building A"
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="plan-description">Description</Label>
              <Textarea
                id="plan-description"
                value={formDescription}
                onChange={(e) => { setFormDescription(e.target.value); }}
                placeholder="Describe the purpose and scope of this plan."
                rows={4}
              />
            </div>
            <label className="flex items-start gap-2.5 cursor-pointer">
              <Checkbox
                checked={formIsTemplate}
                onCheckedChange={(checked) => { setFormIsTemplate(checked === true); }}
                className="mt-0.5"
              />
              <span>
                <span className="text-sm font-medium">Reusable template</span>
                <span className="block text-xs text-muted-foreground">
                  Templates are blueprints — build the steps once with device placeholders, then
                  instantiate them into ready-to-run changes.
                </span>
              </span>
            </label>
          </form>
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button
            variant="outline"
            onClick={() => { setIsCreateOpen(false); }}
            disabled={createMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="plan-create-form"
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? 'Creating…' : 'Create plan'}
          </Button>
        </AppSheet.Footer>
      </AppSheet>

      <ConfirmationModal
        isActive={!!planToDelete}
        title="Delete plan"
        message={`Delete "${planToDelete?.title ?? 'this plan'}"? This permanently removes the plan and its steps and can't be undone.`}
        confirmText="Delete"
        isDanger
        isLoading={deleteMutation.isPending}
        onConfirm={() => { void confirmDelete(); }}
        onCancel={() => { setPlanToDelete(null); }}
      />
    </PageContainer>
  );
}
